import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { 
  sendEmailNotification, 
  sendToCoaches, 
  getNotificationHistory,
  getDashboardStats,
  getAttendanceStats,
  trackFeatureUsage 
} from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
  Mail,
  Send,
  Users,
  Bell,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';

export const NotificationsPage = () => {
  const { isAdmin, gym } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Form states
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [notificationType, setNotificationType] = useState('alert');
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
    trackFeatureUsage('notifications', 'view').catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      const response = await getNotificationHistory();
      setHistory(response.data);
    } catch (error) {
      // Notification history might not exist yet
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!recipientEmail || !subject) {
      toast.error('Please fill in recipient email and subject');
      return;
    }
    
    setSending(true);
    try {
      await sendEmailNotification({
        recipient_email: recipientEmail,
        subject,
        notification_type: notificationType,
        content: { message }
      });
      
      toast.success('Email sent successfully!');
      setRecipientEmail('');
      setSubject('');
      setMessage('');
      loadData();
      trackFeatureUsage('notifications', 'send_email').catch(() => {});
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send email';
      if (detail.includes('not configured')) {
        toast.error('Email service not configured. Please add your Resend API key.');
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendSummary = async () => {
    setSending(true);
    try {
      // Get current stats
      const [statsRes, attendanceRes] = await Promise.all([
        getDashboardStats(),
        getAttendanceStats()
      ]);
      
      const stats = statsRes.data;
      const attendance = attendanceRes.data;
      
      await sendToCoaches(
        `Daily Summary - ${gym?.name || 'Black Arrow MMA'}`,
        'summary',
        {
          fighters_count: stats.fighters_count,
          active_camps: stats.active_camps,
          upcoming_fights: stats.upcoming_fights,
          check_ins_today: attendance.today_check_ins
        }
      );
      
      toast.success('Summary sent to all coaches!');
      loadData();
      trackFeatureUsage('notifications', 'send_summary').catch(() => {});
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send summary';
      if (detail.includes('not configured')) {
        toast.error('Email service not configured. Please add your Resend API key.');
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendWeeklyReport = async () => {
    setSending(true);
    try {
      await sendToCoaches(
        `Weekly Report - ${gym?.name || 'Black Arrow MMA'}`,
        'report',
        {
          new_assessments: 5,
          fights_completed: 2,
          total_check_ins: 45,
          win_rate: '75%'
        }
      );
      
      toast.success('Weekly report sent to all coaches!');
      loadData();
      trackFeatureUsage('notifications', 'send_report').catch(() => {});
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send report';
      if (detail.includes('not configured')) {
        toast.error('Email service not configured. Please add your Resend API key.');
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-white mb-2">ADMIN ONLY</h2>
          <p className="text-zinc-500 font-body">Only administrators can access email notifications</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Communication</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
            EMAIL NOTIFICATIONS
          </h1>
          <p className="text-zinc-500 mt-2 font-body">Send notifications and reports to your coaches</p>
        </div>

        <Tabs defaultValue="send" className="space-y-6">
          <TabsList className="bg-ba-surface border border-ba-border p-1">
            <TabsTrigger value="send" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-body">
              <Send className="w-4 h-4 mr-2" />
              Send Email
            </TabsTrigger>
            <TabsTrigger value="quick" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-body">
              <Bell className="w-4 h-4 mr-2" />
              Quick Actions
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-body">
              <Clock className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Send Email Tab */}
          <TabsContent value="send">
            <div className="bg-ba-surface border border-ba-border p-6 rounded-sm">
              <form onSubmit={handleSendEmail} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Recipient Email</label>
                    <Input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="coach@example.com"
                      className="h-12 bg-ba-black border-ba-border"
                      data-testid="recipient-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Notification Type</label>
                    <Select value={notificationType} onValueChange={setNotificationType}>
                      <SelectTrigger className="h-12 bg-ba-black border-ba-border" data-testid="notification-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alert">Alert</SelectItem>
                        <SelectItem value="summary">Summary</SelectItem>
                        <SelectItem value="report">Report</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Subject</label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject..."
                    className="h-12 bg-ba-black border-ba-border"
                    data-testid="email-subject"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Message</label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write your message here..."
                    className="min-h-32 bg-ba-black border-ba-border resize-none"
                    data-testid="email-message"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" /> Send Email</>
                  )}
                </Button>
              </form>
            </div>
          </TabsContent>

          {/* Quick Actions Tab */}
          <TabsContent value="quick">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-ba-surface border border-ba-border p-6 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-600/10 border border-blue-600/20 rounded-sm flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white">DAILY SUMMARY</h3>
                    <p className="text-zinc-500 text-sm font-body">Send today's stats to all coaches</p>
                  </div>
                </div>
                <Button
                  onClick={handleSendSummary}
                  disabled={sending}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white uppercase font-heading tracking-wider transition-all"
                  data-testid="send-summary-btn"
                >
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Users className="w-4 h-4 mr-2" /> Send to All Coaches</>
                  )}
                </Button>
              </div>

              <div className="bg-ba-surface border border-ba-border p-6 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-purple-600/10 border border-purple-600/20 rounded-sm flex items-center justify-center">
                    <Bell className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white">WEEKLY REPORT</h3>
                    <p className="text-zinc-500 text-sm font-body">Send performance report to all coaches</p>
                  </div>
                </div>
                <Button
                  onClick={handleSendWeeklyReport}
                  disabled={sending}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white uppercase font-heading tracking-wider transition-all"
                  data-testid="send-report-btn"
                >
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Users className="w-4 h-4 mr-2" /> Send to All Coaches</>
                  )}
                </Button>
              </div>
            </div>

            {/* Setup Notice */}
            <div className="bg-yellow-600/10 border border-yellow-600/30 p-4 rounded-sm mt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-200 font-body text-sm font-medium">Email Service Setup Required</p>
                  <p className="text-yellow-200/70 font-body text-sm mt-1">
                    To send emails, you need to add a Resend API key to your environment. 
                    Sign up free at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-yellow-400 underline">resend.com</a> and 
                    add your key as RESEND_API_KEY in the backend .env file.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="bg-ba-surface border border-ba-border rounded-sm">
              {history.length > 0 ? (
                <div className="divide-y divide-ba-border">
                  {history.map((notification, index) => (
                    <motion.div
                      key={notification.id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 flex items-center gap-4"
                    >
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center",
                        notification.status === 'sent' ? "bg-green-600/20" : "bg-red-600/20"
                      )}>
                        {notification.status === 'sent' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-body truncate">{notification.subject}</p>
                        <p className="text-zinc-500 text-sm font-body">To: {notification.recipient}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-zinc-500 text-sm font-body">
                          {notification.sent_at ? format(parseISO(notification.sent_at), 'M/d, h:mm a') : 'N/A'}
                        </p>
                        <p className={cn(
                          "text-xs uppercase tracking-wider",
                          notification.type === 'alert' ? "text-yellow-500" :
                          notification.type === 'summary' ? "text-blue-500" :
                          "text-purple-500"
                        )}>
                          {notification.type}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Mail className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 font-body">No emails sent yet</p>
                  <p className="text-zinc-600 text-sm font-body mt-1">Your notification history will appear here</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
